import { useEffect } from 'react';
import * as React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { BookingEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import useAddOns from 'Hooks/useAddOns';
import useAsync from 'Hooks/useAsync';
import { GetEventSeriesWithOccurrences } from 'Services/Api/_HumanWritten/EventSeriesEntityService';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';
import { getMessageByKey } from 'Stores/LanguageStore';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import CompletePaymentModal from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/CompletePaymentModal';
import { useBookingWizardDataStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { WhiteBackground } from 'Views/Components/_HumanWritten/WhiteBackground';
import If from 'Views/Components/If/If';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { canCancelBooking, canEditBooking } from './Helpers/CanEditBooking';
import {
	BookingRestriction,
	GetFeeWaiverBox,
	GetInformationBox,
	GetStaffRestrictionsBox,
	StateOfBooking,
} from './Helpers/GetInformationBox';
import { onEdit } from './Helpers/OnEdit';
import { onEventEdit } from './Helpers/OnEventEdit';
import { BookingNotesModal } from './NotesModal/BookingNotesModal';
import BookingActionButtons from './BookingActionButtons';
import { SectionProps } from './SharedSections/BookingDetailSection';
import { BookingDetailedViewHeader } from './BookingDetailedViewHeader';
import {
	ComponentPropsConfig,
	fieldComponents,
	getBookingDetailedViewComponents,
} from './BookingDetailedViewComponentProps';
import { BOOKING_DETAILED_VIEW_PAGE_STAFF_TITLE, BOOKING_DETAILED_VIEW_PAGE_CUSTOMER_TITLE } from 'ConstantPageNames';
import { appConfigStore } from 'Models/AppConfigStore';
import { RefundsSection } from './RefundsSection';
import { EditType } from './BookingDetailedView';
import { getBookingEditOptions, hasEditableReturnLeg } from './Modals/EditBooking/GetBookingEditOptions';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';

export interface BookingDetailedViewInnerProps {
	booking: BookingEntity;
	refresh: () => void;
}

function BookingDetailedViewInner({ booking, refresh }: BookingDetailedViewInnerProps) {
	const navigate = useNavigate();

	const [completePaymentOpen, setCompletePaymentOpen] = React.useState(false);

	const { setWizardData, setOldWizardData } = useBookingWizardDataStore();

	// Set document title
	useEffect(() => {
		document.title = BOOKING_DETAILED_VIEW_PAGE_CUSTOMER_TITLE;
		if (store.isStaff) {
			document.title = BOOKING_DETAILED_VIEW_PAGE_STAFF_TITLE;
		}
	}, []);

	// The wizard's mobile layout kicks in well above phone widths — passenger-count edits
	// must land on the search step whenever that layout is active (see redirectEditTo).
	const { isMobileBookingWizard } = useDeviceDetect();

	// Ensure the add-on store is loaded so the guided edit flow can offer the add-ons option
	// when the app has add-ons for this booking type (re-renders this view once it resolves).
	useAddOns();

	// Likewise ensure dynamic booking fields are loaded so the "Other" edit option can appear.
	// Subscribing to `loaded` re-renders the view (and recomputes the edit options) once ready.
	const dynamicFieldsLoaded = useDynamicBookingFieldsStore(s => s.loaded);
	const loadDynamicFields = useDynamicBookingFieldsStore(s => s.loadFields);
	useEffect(() => {
		if (!dynamicFieldsLoaded) {
			loadDynamicFields();
		}
	}, [dynamicFieldsLoaded, loadDynamicFields]);
	const { isInvoiced } = booking;
	const typeOfBooking = booking.typeOfBooking();

	// Event bookings can only change their date/time when the series actually has more than one
	// occurrence to switch to. Fetch the series' upcoming occurrences (event bookings only) so the
	// guided edit flow can hide the "Date & time" option when there's a single date/time.
	// (Kept above the early return below to satisfy the rules of hooks.)
	//
	// The series id only lives on ferryTrip.eventDetail — bookedSummary.eventId is the *occurrence*
	// (event detail) id, so it must not be used here (it would 404 the series lookup).
	const eventSeriesId = booking.bookedSummary?.ferryTrip?.eventDetail?.eventSeriesId;
	const eventSeriesResponse = useAsync(
		() =>
			stringNotEmpty(eventSeriesId) ? GetEventSeriesWithOccurrences(eventSeriesId) : Promise.resolve(undefined),
		[eventSeriesId],
	);
	// The all-occurrences endpoint returns the full list but leaves upcomingOccurrenceCount at 0
	// (it's a non-nullable int it never sets), so take whichever source is larger. Default to
	// showing the option (fail open) — only hide it once we've positively confirmed the series has
	// a single occurrence to switch between.
	const knownOccurrenceCount = Math.max(
		eventSeriesResponse.data?.upcomingOccurrenceCount ?? 0,
		eventSeriesResponse.data?.upcomingOccurrences?.length ?? 0,
	);
	const hasMultipleEventDateTimes = !(
		eventSeriesResponse.type === 'data' &&
		isNotNullOrUndefined(eventSeriesResponse.data) &&
		knownOccurrenceCount <= 1
	);

	// IMPORTANT - we need booked summary for this component to work
	if (isNullOrUndefined(booking.bookedSummary)) {
		alertToast('Booking not found', 'error', undefined, { autoClose: 3000 });
		return <Navigate to="/404" />;
	}

	const canEdit = canEditBooking(booking);
	const canCancel = canCancelBooking(booking);

	const isEventBooking = isNotNullOrUndefined(booking.bookedSummary.eventId);
	const isDeparted = booking.bookedSummary.ferryTrip.departureDateTime < new Date();
	const bookingTypeIsClosed = booking.bookedSummary.ferryTrip.isClosedForSpecificBookingType(booking.typeOfBooking());
	const { checkedIn, checkInStatusId } = booking;

	// Every restriction that applies, not just the first — a checked-in driverless vehicle booking
	// can be neither cancelled nor edited, and naming only one of those leaves the other a mystery.
	const statesOfBooking: StateOfBooking[] = [];
	if (checkedIn || isNotNullOrUndefined(checkInStatusId)) {
		statesOfBooking.push('checked-in');
	}
	if (isInvoiced) {
		statesOfBooking.push('invoiced');
	}
	if (bookingTypeIsClosed) {
		statesOfBooking.push('closed');
	}
	if (!booking.hasPassengers() && appConfigStore.DriverlessVehicleBookingPriceIncludesAdultPrice) {
		statesOfBooking.push('driverless_restriction');
	}

	// Staff see the customer's restrictions plus the ones that quietly remove their own actions.
	// Without these, an "Edit this booking" that has vanished has no explanation anywhere on the
	// page. Order mirrors the gates in canEditBooking / canModifyBooking.
	const isMigratedAndLocked = !appConfigStore.migratedBookingsModificationEnabled && !!booking.legacyId;
	const isCheckInFinalised =
		appConfigStore.lockChangesWhenCheckInFinalised && (booking.bookedSummary.ferryTrip?.checkInFinalised ?? false);
	const staffRestrictions: BookingRestriction[] = [
		...(statesOfBooking.filter(x => x !== 'standard') as BookingRestriction[]),
	];
	if (booking.isFreightBooking()) {
		staffRestrictions.push('freight');
	}
	if (isMigratedAndLocked) {
		staffRestrictions.push('migrated');
	}
	if (booking.transactionStatus === 'UNPAID') {
		staffRestrictions.push('unpaid');
	}
	if (isCheckInFinalised) {
		staffRestrictions.push('check-in-finalised');
	}
	if (!store.mayMakeBookings) {
		staffRestrictions.push('no-booking-permission');
	}
	const alterationRulesOff = booking.bookedSummary.ferryTrip?.alterationRulesDisabled ?? false;
	const cancellationRulesOff = booking.bookedSummary.ferryTrip?.cancellationRulesDisabled ?? false;

	const sectionProps = {
		booking: booking,
		canEdit: canEdit,
		onEdit: editType => {
			// Use event-specific edit handler for event bookings
			if (isEventBooking) {
				onEventEdit(navigate, booking, editType);
			} else {
				onEdit(
					navigate,
					setWizardData,
					setOldWizardData,
					booking,
					editType,
					bookingIsDepartureOfTwoWayBooking,
					isMobileBookingWizard,
				);
			}
		},
		isInvoiced: isInvoiced,
		showQrCode: booking.bookingStatus !== 'CANCELLED',
	} as SectionProps;

	// True if the current booking is the departure leg of a two way booking.
	// False means current booking is the return leg of a two way booking OR has no associated booking.
	const bookingIsDepartureOfTwoWayBooking =
		(isNotNullOrUndefined(booking.returnBooking) && isNullOrUndefined(booking.returnBookingFor)) ||
		(isNullOrUndefined(booking.returnBooking) && isNullOrUndefined(booking.returnBookingFor));

	const dynamicPropsConfig = getBookingDetailedViewComponents(sectionProps, booking, typeOfBooking, refresh);
	const fieldsToRender = dynamicPropsConfig[typeOfBooking] || [];

	// Guided edit flow — the editable items shown in the "Edit booking" wizard, and whether to
	// offer the "this trip / both trips" choice for two-way bookings.
	const editOptions = getBookingEditOptions(booking, hasMultipleEventDateTimes);
	const hasReturnLegToEdit = hasEditableReturnLeg(booking);

	const handleSelectEdit = (editType: EditType, alterReturn: boolean) => {
		if (isEventBooking) {
			onEventEdit(navigate, booking, editType);
		} else {
			onEdit(
				navigate,
				setWizardData,
				setOldWizardData,
				booking,
				editType,
				bookingIsDepartureOfTwoWayBooking,
				isMobileBookingWizard,
				alterReturn,
			);
		}
	};

	const unpaidValue = booking.bookedSummary.totalCost - booking.bookedSummary.paidBookingPrice;
	const pendingRefunds =
		booking.bookedSummary?.refunds?.filter(
			x => x?.status !== 'COMPLETED' && x?.status !== 'MANUALLY_COMPLETED' && x?.status !== 'CANCELLED',
		) ?? [];

	return (
		<WhiteBackground
			content={
				<div className={`booking-detailed-view__container ${store.isStaff ? 'bottom-padding' : ''}`}>
					<BookingDetailedViewHeader
						booking={booking}
						bookedSummary={booking.bookedSummary}
						typeOfBooking={typeOfBooking}
					/>
					<div className="booking-details__breakdown">
						{/* Staff get this as a row of their restrictions box below. Customers stop seeing it after departure. */}
						<If condition={!store.isStaff && !isDeparted && booking.bookingStatus !== 'CANCELLED'}>
							{GetFeeWaiverBox(alterationRulesOff, cancellationRulesOff)}
						</If>

						<If condition={booking.transactionStatus === 'UNPAID'}>
							<MessageBox
								status="warning"
								title={getMessageByKey('unpaid-booking__information-modal__title', 'Payment pending')}
								actions={
									<Button
										onClick={() => setCompletePaymentOpen(true)}
										display={Display.Outline}
										colors={Colors.Black}
										sizes={Sizes.Small}
										newStyles
										buttonProps={{ 'data-test-id': 'booking-complete-payment-button' } as any}
									>
										Complete payment
									</Button>
								}
							>
								<p>
									<FormattedMessage
										id="unpaid-booking__information-modal__body"
										defaultMessage="This booking is confirmed but not yet paid and can not be modified until payment is complete. {unpaidAmount} unpaid."
										values={{
											unpaidAmount: `${formatPriceDisplay(unpaidValue, false)}`,
										}}
									/>
								</p>
							</MessageBox>
						</If>

						<RefundsSection pendingRefunds={pendingRefunds} refresh={refresh} />

						{/*
						 * Staff see their version departed or not, so they can always tell what state the
						 * booking is in and why an action is missing. It also absorbs the finalised
						 * check-in and migrated-booking notices, so staff get one box rather than three.
						 */}
						<If condition={store.isStaff && booking.bookingStatus !== 'CANCELLED'}>
							{GetStaffRestrictionsBox(
								staffRestrictions,
								booking.checkInStatus?.label ?? 'checked in',
								alterationRulesOff,
								cancellationRulesOff,
							)}
						</If>
						{/*
						 * Customers only see this while the trip is still upcoming — after departure
						 * there's nothing they could do about it anyway.
						 */}
						<If
							condition={
								!store.isStaff &&
								!isDeparted &&
								booking.bookingStatus !== 'CANCELLED' &&
								statesOfBooking.length > 0
							}
						>
							{GetInformationBox(statesOfBooking, booking.checkInStatus?.label ?? 'checked in')}
						</If>
						<If condition={!store.isStaff && statesOfBooking.length === 0 && isMigratedAndLocked}>
							<MessageBox title={appConfigStore.migratedBookingsModificationInfoTitle} status="info">
								{appConfigStore.migratedBookingsModificationInfoBody}
							</MessageBox>
						</If>
						{fieldsToRender.map((fieldConfig, index) => {
							const { name, props } = fieldConfig;
							const Component = fieldComponents[name];
							return Component ? (
								// @ts-expect-error - spread props aren't narrowable across the fieldComponents union
								<Component key={name} {...(props as ComponentPropsConfig[typeof name])} />
							) : null;
						})}
						<If
							condition={
								(booking.bookedSummary.parkingReservations?.length ?? 0) > 0 ||
								(booking.returnBookingFor?.bookedSummary?.parkingReservations?.length ?? 0) > 0
							}
						>
							<MessageBox
								title="Parking reservation"
								status="info"
								onClick={() => {
									const hasOwnParking = (booking.bookedSummary!.parkingReservations?.length ?? 0) > 0;
									const targetId = hasOwnParking ? booking.id : booking.returnBookingFor?.id;
									if (targetId) {
										navigate(`/bookings/${targetId}/parking`);
									}
								}}
							>
								{'This booking has parking included. Tap to view details.'}
							</MessageBox>
						</If>
					</div>
					<BookingActionButtons
						booking={booking}
						canModify={canCancel}
						canEdit={canEdit}
						editOptions={editOptions}
						isTwoWay={hasReturnLegToEdit}
						onSelectEdit={handleSelectEdit}
						customStatus={booking.transactionStatus}
						refresh={refresh}
						isEventBooking={isEventBooking}
					/>
					<If condition={store.isStaff || store.isInvoicedUser}>
						<BookingNotesModal
							booking={booking}
							customerCanViewNotes={booking.user?.userDisplayName?.invoicedUser}
						/>
					</If>
					<If condition={booking.transactionStatus === 'UNPAID'}>
						<CompletePaymentModal
							isOpen={completePaymentOpen}
							onClose={() => setCompletePaymentOpen(false)}
							bookingId={booking.id}
							ownerUserId={booking.userId}
							unpaidValue={unpaidValue}
							onSuccess={refresh}
						/>
					</If>
				</div>
			}
		/>
	);
}

export default observer(BookingDetailedViewInner);
